import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { AutoRefreshTokenService, provideKeycloak, UserActivityService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { provideHttpClient } from '@angular/common/http';
import {
  DATE_INJECTION,
  ERROR_INJECTION,
  FILE_API_LOCATION,
  FILE_TYPE_INJECTION,
  FORM_INJECTION,
  FormValidator,
  INPUT_INJECTION,
  RestSupporter,
  SERVER_URL_INJECTION,
} from '@innovactions/frontend-library';
import { DatePipe } from '@angular/common';
import { ErrorSchema } from './schema/error/error-schema';
import { provideApollo } from 'apollo-angular';
import { createApolloAuthMiddleware } from './supporter/apollo-auth-middleware';
import { ApolloLink, createHttpLink } from '@apollo/client/core';
import { InMemoryCache } from '@apollo/client/cache';
import Keycloak from 'keycloak-js';
import { GraphqlRouting } from './service/graphql-routing';
import { removeTypenameLink } from './supporter/remove-typename-link';
import { ColorSupporter } from './supporter/color-supporter';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import * as Sentry from '@sentry/angular';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: environment.realmId,
        clientId: environment.clientId,
      },
      initOptions: {
        onLoad: 'login-required',
        redirectUri: window.location.href,
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html'
      },
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    provideHttpClient(),
    RestSupporter,
    FormValidator,
    ColorSupporter,
    // Note: this is needed for ngx-graph, there is no alternative for this deprecated dependency
    provideAnimationsAsync(),
    { provide: DATE_INJECTION, useClass: DatePipe },
    { provide: ERROR_INJECTION, useClass: ErrorSchema },
    { provide: INPUT_INJECTION, useValue: null },
    { provide: FORM_INJECTION, useValue: null },
    { provide: SERVER_URL_INJECTION, useValue: environment.serverUrl },
    { provide: FILE_API_LOCATION, useValue: null },
    { provide: FILE_TYPE_INJECTION, useValue: ['.png', '.jpg', '.jpeg', 'application/pdf'] },
    provideApollo(() => {
      const keycloak = inject(Keycloak);
      const routingService = inject(GraphqlRouting);
      const authMiddleware = createApolloAuthMiddleware(
        keycloak,
        routingService,
      );

      const httpLink = createHttpLink({
        uri: (operation) => {
          return (
            environment.serverUrl +
            routingService.resolveUri(operation.operationName)
          );
        },
      });

      return {
        link: ApolloLink.from([authMiddleware, removeTypenameLink, httpLink]),
        cache: new InMemoryCache(),
      };
    }),
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() }
  ],
};
